import uuid4 from "uuid4";
import {createEmptyScoringConfig, scoringConfigFromApi, scoringConfigToApi} from "@/scoringUi";

// --- Form mapping helpers (API → form state) ---

const mapPromptToListItem = (prompt) => ({id: uuid4(), value: prompt, disabled: true});

const mapTrainingPhrasesToListItems = (phrases) => phrases.map(mapPromptToListItem);

const mapApiEntityToFormEntity = (entity) => ({
    ...entity,
    id: uuid4(),
    examples: entity.prompts?.map(mapPromptToListItem),
    skipPhrases: entity.skipPhrases?.map(mapPromptToListItem),
    transitions: entity.transitions?.map(item => ({...item, id: uuid4()})),
    modifyResponses: entity.modifyResponses ?? false,
    isFinishing: entity.isFinishing ?? false,
    endConversation: entity.endConversation ?? false,
    dynamicWorkerValues: typeof entity.dynamicWorkerValues === 'string' && entity.dynamicWorkerValues
        ? JSON.parse(entity.dynamicWorkerValues)
        : (entity.dynamicWorkerValues ?? []),
    scoring: entity.scoring
        ? scoringConfigFromApi(entity.scoring)
        : {...createEmptyScoringConfig(), criterionKey: uuid4()},
});

const mapApiEntitiesToFormEntities = (entities) => entities.map(mapApiEntityToFormEntity);

const normalizeMentionResponse = (str) => {
    let result = str.replace(/(?<!\{)#([\w_]+)/g, '{#$1}');
    result = result.replace(/(?<!\{)\$([\w_]+)/g, '{$$1}');
    return result;
};

// --- API format mapping (form state → API) ---

const mapNonEmptyValues = (array) => {
    return array
        .filter(item => item.value !== null && item.value !== "")
        .map(item => item.value);
};

const mapNonEmptyTransitions = (array) => {
    return array
        .filter(item => item.destination && item.condition);
}

const mapEntitiesToApiFormat = (entities) => {
    return entities?.map(entity => {
        const prompts = mapNonEmptyValues(entity.examples || []);
        const skipPhrases = mapNonEmptyValues(entity.skipPhrases || []);
        const transitions = mapNonEmptyTransitions(entity.transitions || []);

        return {
            name: entity.name,
            displayName: entity.displayName,
            completeResponse: entity.completeResponse,
            response: entity.response,
            nextIntent: entity.nextIntent,
            prompts: prompts,
            skipPhrases: skipPhrases,
            transitions: transitions,
            dynamicValues: entity.dynamicValues?.map(dv => ({
                name: dv.name,
                endpointId: dv.endpointId,
                endpointMethod: dv.endpointMethod,
                endpointUrl: dv.endpointUrl,
                params: dv.params?.flatMap(p => Object.entries(p).map(([name, value]) => ({ name, value }))) ?? [],
                bodySchema: dv.bodySchema ?? {}
            })),
            dynamicWorkerValues: entity.dynamicWorkerValues?.length
                ? JSON.stringify(entity.dynamicWorkerValues.map(dwv => ({
                    name: dwv.name,
                    workerId: dwv.workerId,
                    mappings: dwv.mappings ?? {},
                })))
                : undefined,
            isRequired: entity.isRequired,
            isConditional: entity.isConditional,
            modifyResponses: entity.modifyResponses ?? false,
            isFinishing: entity.isFinishing ?? false,
            endConversation: entity.endConversation ?? false,
            scoring: scoringConfigToApi(entity.scoring),
        };
    });
}

const mapIntentToApiFormat = (inputObject, scenarioId) => {
    const allNonEmptyTrainingPhrases = mapNonEmptyValues(inputObject.trainingPhrases || []);
    const mappedEntities = mapEntitiesToApiFormat(inputObject.entities);

    return {
        scenarioId: scenarioId,
        name: inputObject.name,
        finalResponse: inputObject.finalResponse,
        trainingPhrases: allNonEmptyTrainingPhrases,
        entities: mappedEntities,
        dynamicValues: inputObject.dynamicValues?.map(dv => ({
            name: dv.name,
            endpointId: dv.endpointId,
            endpointMethod: dv.endpointMethod,
            endpointUrl: dv.endpointUrl,
            params: dv.params?.flatMap(p => Object.entries(p).map(([name, value]) => ({ name, value }))) ?? [],
            bodySchema: dv.bodySchema ?? {}
        })),
        dynamicWorkerValues: inputObject.dynamicWorkerValues?.length
            ? JSON.stringify(inputObject.dynamicWorkerValues.map(dwv => ({
                name: dwv.name,
                workerId: dwv.workerId,
                mappings: dwv.mappings ?? {},
            })))
            : undefined,
    };
}

const mapTransitionToCondition = (transition) => {
    return `if ${transition.condition} THEN ${transition.destination}`
}

const mapTransitionsToValidResponse = (transitions) => {
    const conditions = transitions.map(transition => {
        return mapTransitionToCondition(transition);
    })

    return conditions.join(" || ");
}

export {mapPromptToListItem, mapTrainingPhrasesToListItems, mapApiEntityToFormEntity, mapApiEntitiesToFormEntities, normalizeMentionResponse};

export default {
    mapIntentToApiFormat,
    mapTransitionToCondition,
    mapTransitionsToValidResponse
};
